import { useEffect, useRef, useState } from 'react'
import Modal from 'react-modal'
import { UpdateVenueLocationRequestDto, VenueDetailDto } from '@ivy-monorepo/shared'
import { adminService } from 'src/services/admin.service'
import { BaseModalProps } from 'src/types/modal'
import { loadGoogleMaps } from 'src/utils/googleMaps'

type EditVenueLocationModalProps = BaseModalProps & {
  venuePublicId: string
  latitude: number
  longitude: number
  address: string
  postalCode?: string | null
  northeastLatitude?: number
  northeastLongitude?: number
  southwestLatitude?: number
  southwestLongitude?: number
  onSaved: (venue: VenueDetailDto) => void
}

const EditVenueLocationModal = ({
  isOpen,
  onClose,
  venuePublicId,
  latitude,
  longitude,
  address,
  postalCode,
  northeastLatitude,
  northeastLongitude,
  southwestLatitude,
  southwestLongitude,
  onSaved,
}: EditVenueLocationModalProps) => {
  const mapContainerRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<any>(null)
  const markerRef = useRef<any>(null)

  const [formLatitude, setFormLatitude] = useState('')
  const [formLongitude, setFormLongitude] = useState('')
  const [formAddress, setFormAddress] = useState('')
  const [formPostalCode, setFormPostalCode] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [mapReady, setMapReady] = useState(false)

  useEffect(() => {
    if (!isOpen) {
      return
    }
    setFormLatitude(String(latitude ?? ''))
    setFormLongitude(String(longitude ?? ''))
    setFormAddress(address ?? '')
    setFormPostalCode(postalCode ?? '')
    setError(null)
    setIsSaving(false)
  }, [isOpen, latitude, longitude, address, postalCode])

  useEffect(() => {
    if (!isOpen) {
      return
    }

    let cancelled = false

    loadGoogleMaps()
      .then(() => {
        if (cancelled || !mapContainerRef.current) {
          return
        }

        const center = { lat: latitude, lng: longitude }
        const map = new window.google.maps.Map(mapContainerRef.current, {
          center,
          zoom: 16,
          mapTypeId: 'roadmap',
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        })
        mapRef.current = map

        const marker = new window.google.maps.Marker({
          map,
          position: center,
          draggable: true,
          title: 'Venue location',
        })
        markerRef.current = marker

        const applyPosition = (lat: number, lng: number) => {
          setFormLatitude(lat.toFixed(6))
          setFormLongitude(lng.toFixed(6))
        }

        marker.addListener('dragend', () => {
          const position = marker.getPosition()
          if (!position) return
          applyPosition(position.lat(), position.lng())
        })

        map.addListener('click', (event: any) => {
          const lat = event.latLng?.lat()
          const lng = event.latLng?.lng()
          if (typeof lat === 'number' && typeof lng === 'number') {
            marker.setPosition({ lat, lng })
            applyPosition(lat, lng)
          }
        })

        setMapReady(true)
      })
      .catch(() => {
        setError('Map failed to load. You can still enter coordinates manually.')
        setMapReady(false)
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) {
      markerRef.current?.setMap(null)
      markerRef.current = null
      mapRef.current = null
      setMapReady(false)
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen || !mapRef.current || !markerRef.current) {
      return
    }
    const lat = Number(formLatitude)
    const lng = Number(formLongitude)
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return
    }
    const current = markerRef.current.getPosition()
    if (current && current.lat().toFixed(6) === lat.toFixed(6) && current.lng().toFixed(6) === lng.toFixed(6)) {
      return
    }
    markerRef.current.setPosition({ lat, lng })
    mapRef.current.setCenter({ lat, lng })
  }, [isOpen, formLatitude, formLongitude])

  const canSubmit =
    formAddress.trim().length > 0 &&
    Number.isFinite(Number(formLatitude)) &&
    Number.isFinite(Number(formLongitude)) &&
    !isSaving

  const submit = async () => {
    if (!canSubmit) {
      return
    }

    const nextLatitude = Number(formLatitude)
    const nextLongitude = Number(formLongitude)

    const payload: UpdateVenueLocationRequestDto = {
      latitude: nextLatitude,
      longitude: nextLongitude,
      address: formAddress.trim(),
      postalCode: formPostalCode.trim() || null,
    }

    const hasOriginalViewport =
      northeastLatitude != null && northeastLongitude != null && southwestLatitude != null && southwestLongitude != null

    if (hasOriginalViewport) {
      // Preserve the existing viewport's size/shape by translating it to the new point.
      const deltaLatitude = nextLatitude - latitude
      const deltaLongitude = nextLongitude - longitude
      payload.northeastLatitude = northeastLatitude + deltaLatitude
      payload.northeastLongitude = northeastLongitude + deltaLongitude
      payload.southwestLatitude = southwestLatitude + deltaLatitude
      payload.southwestLongitude = southwestLongitude + deltaLongitude
    }

    setIsSaving(true)
    setError(null)
    try {
      const updated = await adminService.updateVenueLocation(venuePublicId, payload)
      onSaved(updated)
      onClose()
    } catch (e: any) {
      const message = e?.response?.data?.message || e?.response?.data?.error || 'Failed to update location'
      setError(typeof message === 'string' ? message : 'Failed to update location')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Modal isOpen={isOpen} className="modal-popup edit-venue-location-popup" overlayClassName="modal-overlay" onRequestClose={onClose}>
      <div className="modal-content-wrapper">
        <h2>Edit Location</h2>

        {isSaving && (
          <div className="loader-wrapper">
            <div className="loader" />
          </div>
        )}

        <div ref={mapContainerRef} className="edit-venue-location-map" style={{ width: '100%', height: 320, borderRadius: 4 }} />
        {!mapReady && <div className="add-venue-map-fallback">Map unavailable — enter coordinates manually.</div>}
        <p className="add-venue-hint">Drag the pin or click the map to move it.</p>

        <div className="form-group">
          <label>Address</label>
          <input value={formAddress} onChange={e => setFormAddress(e.target.value)} placeholder="Street address" />
        </div>

        <div className="location-inputs">
          <div className="form-group">
            <label>Latitude</label>
            <input value={formLatitude} onChange={e => setFormLatitude(e.target.value)} placeholder="41.8827" />
          </div>
          <div className="form-group">
            <label>Longitude</label>
            <input value={formLongitude} onChange={e => setFormLongitude(e.target.value)} placeholder="-87.6233" />
          </div>
        </div>

        <div className="form-group">
          <label>Postal code</label>
          <input value={formPostalCode} onChange={e => setFormPostalCode(e.target.value)} placeholder="Optional" />
        </div>

        {error && <div className="add-venue-error">{error}</div>}

        <div className="modal-footer-content">
          <button type="button" className="styled-button sm" onClick={onClose} disabled={isSaving}>
            <span>Cancel</span>
          </button>
          <button type="button" className="styled-button sm" onClick={() => void submit()} disabled={!canSubmit}>
            <span>{isSaving ? 'Saving…' : 'Save location'}</span>
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default EditVenueLocationModal
